import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

type MiddleCategoryType = 'vocabulary' | 'literature' | 'nonLiterature' | 'grammar';
type HighCategoryType = 'regular' | 'clinic';

export default function KoreanCurriculumPage() {
  const [courseType, setCourseType] = useState<'middle' | 'high'>('middle');
  const [middleCategory, setMiddleCategory] = useState<MiddleCategoryType>('vocabulary');
  const [highCategory, setHighCategory] = useState<HighCategoryType>('regular');
  const [currentBookSlide, setCurrentBookSlide] = useState(0);

  const bookImages = [
    {
      src: "https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/9757610894a1326225e19f7943dcec1d.png",
      alt: "이감 국어 독서 교재"
    },
    {
      src: "https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/3e4a0e9be691e2b87625ee65d0f0acbd.png",
      alt: "이감 국어 교재"
    },
    {
      src: "https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/f14a11132defd4863b07055bc6e0a97a.png",
      alt: "이감 국어 교재"
    }
  ];

  // Set page title and meta tags
  useEffect(() => {
    document.title = '미래인재학원 국어 커리큘럼';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '📚 이감 모의고사 도입! 학교별 맞춤 교재로 내신 완벽 대비. 문학·비문학·문법 영역별 체계적 수업. 중등 4개 영역 및 고등 정규·클리닉 과정 운영!');
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', '미래인재학원 국어 커리큘럼');
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', '📚 이감 모의고사 도입! 학교별 맞춤 교재로 내신 완벽 대비. 문학·비문학·문법 영역별 체계적 수업. 중등 4개 영역 및 고등 정규·클리닉 과정 운영!');
    }

    // Schema.org Course structured data
    const existingSchema = document.getElementById('schema-korean-course');
    if (!existingSchema) {
      const script = document.createElement('script');
      script.id = 'schema-korean-course';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Course",
        "name": "미래인재학원 국어 커리큘럼",
        "description": "어휘, 문법, 문학, 비문학 영역별 체계적인 국어 교육 과정. 이감 모의고사 교재 도입, 학교별 맞춤 자체 교재로 내신과 수능을 동시에 대비합니다.",
        "provider": {
          "@type": "EducationalOrganization",
          "name": "미래인재학원",
          "url": "https://eliteoffuture1.com/"
        },
        "educationalLevel": ["중학교", "고등학교"],
        "about": {
          "@type": "Thing",
          "name": "국어"
        },
        "hasCourseInstance": [
          {
            "@type": "CourseInstance",
            "name": "중등 국어 과정",
            "description": "어휘, 문법, 문학, 비문학 4개 영역 학년별 체계적 수업. 빠작, 중등 수능 독해 등 검증된 교재 사용.",
            "courseMode": "onsite",
            "location": {
              "@type": "Place",
              "name": "미래인재학원 (지행, 옥정, 민락 캠퍼스)"
            }
          },
          {
            "@type": "CourseInstance",
            "name": "고등 국어 정규 과정 (주2회)",
            "description": "학교별 레벨별 분반 수업, 학교별 맞춤 자체 교재, 수행평가 대비, 이감 모의고사 교재 활용 수능 수업.",
            "courseMode": "onsite"
          },
          {
            "@type": "CourseInstance",
            "name": "고등 국어 클리닉 (주1회)",
            "description": "어휘 점검테스트, 실전 모의고사 연습, 문학/비문학/언어와 매체 영역별 집중 보완.",
            "courseMode": "onsite"
          }
        ]
      });
      document.head.appendChild(script);
    }

    return () => {
      const schemaEl = document.getElementById('schema-korean-course');
      if (schemaEl) schemaEl.remove();
    };
  }, []);

  // 자동 슬라이드 효과
  useEffect(() => {
    if (courseType === 'high' && highCategory === 'regular') {
      const interval = setInterval(() => {
        setCurrentBookSlide((prev) => (prev + 1) % bookImages.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [courseType, highCategory, bookImages.length]);

  const middleCategories = [
    { id: 'vocabulary' as MiddleCategoryType, name: '어휘' },
    { id: 'grammar' as MiddleCategoryType, name: '문법' },
    { id: 'literature' as MiddleCategoryType, name: '문학' },
    { id: 'nonLiterature' as MiddleCategoryType, name: '비문학' },
  ];

  const highCategories = [
    { id: 'regular' as HighCategoryType, name: '정규수업' },
    { id: 'clinic' as HighCategoryType, name: '클리닉' },
  ];

  const middleCategoryContent = {
    vocabulary: {
      title: '어휘 학습',
      description: '탄탄한 어휘력으로 국어 실력의 기초를 다집니다',
    },
    literature: {
      title: '문학 학습',
      description: '작품 분석력과 감상 능력을 키웁니다',
    },
    nonLiterature: {
      title: '비문학 학습',
      description: '논리적 독해력과 사고력을 향상시킵니다',
    },
    grammar: {
      title: '문법 학습',
      description: '국어 문법의 체계를 완벽하게 정립합니다',
    }
  };

  const highCategoryContent = {
    regular: {
      title: '정규수업',
      description: '내신과 수능을 체계적으로 대비하는 정규 커리큘럼',
      items: [
        {
          name: '내신수업',
          grade: '고등 전학년',
          content: [
            '학교별 + 레벨별 분반 수업',
            '학교별 맞춤 자체 교재',
            '학교별 수행평가 대비',
            '학교별 자체 제작 모의고사로 모의고사 시행'
          ]
        },
        {
          name: '수능수업',
          grade: '고등 전학년',
          content: [
            '모의고사 수업',
            '어휘, 문학, 비문학, 문법 영역별 수업 진행',
            '이감학원 프로그램 도입'
          ]
        }
      ]
    },
    clinic: {
      title: '클리닉',
      description: '개인별 취약점을 집중 보완하는 맞춤형 수업',
      items: [
        {
          name: '',
          grade: '고등 전학년',
          content: [
            '현대시 해석 집중 훈련',
            '고전시가 독해력 향상',
            '소설 서사 구조 분석',
            '문학 개념어 완벽 정리',
            '작품 비교 분석 훈련',
            '서술형 답안 작성법'
          ]
        },
        {
          name: '',
          grade: '고등 전학년',
          content: [
            '지문 구조 분석 훈련',
            '추론 능력 향상 훈련',
            '고난도 지문 정복',
            '선지 분석 능력 강화',
            '독해 속도 향상',
            '영역별 배경지식 확충'
          ]
        },
        {
          name: '언어와 매체 클리닉',
          grade: '고등 전학년',
          content: [
            '음운론 집중 훈련',
            '형태론/통사론 완성',
            '국어사 핵심 정리',
            '매체 유형별 분석',
            '문법 기출 완벽 분석',
            '고난도 문법 문제 공략'
          ]
        }
      ]
    }
  };

  // 고등과정 콘텐츠
  const highContents = {
    regular: {
      title: '정규수업',
      description: '주2회 수업',
      items: [
        {
          name: '고1 정규',
          description: '고등 국어의 기초를 다지는 체계적인 수업',
          features: ['문학 작품 분석', '독서 지문 독해', '문법 기초 완성', '내신 대비'],
        },
        {
          name: '고2 정규',
          description: '수능 대비와 내신을 동시에 준비하는 심화 과정',
          features: ['수능형 문학 분석', '비문학 독해 훈련', '문법 심화', '학교별 내신 대비'],
        },
        {
          name: '고3 정규',
          description: '수능 완성을 위한 최종 마무리 과정',
          features: ['수능 실전 문제 풀이', '시간 관리 훈련', '취약 영역 집중', '파이널 모의고사'],
        },
      ],
    },
    clinic: {
      title: '클리닉',
      description: '취약 영역을 집중 보완하는 맞춤형 수업',
      items: [
        {
          name: '문학 클리닉',
          description: '문학 작품 해석과 문제 풀이 능력 향상',
          features: ['현대시 분석', '고전시가 해석', '소설 구조 파악', '극/수필 이해'],
        },
        {
          name: '독서 클리닉',
          description: '비문학 지문 독해력 집중 향상',
          features: ['인문 지문 독해', '사회 지문 분석', '과학/기술 지문', '예술 지문 이해'],
        },
        {
          name: '문법 클리닉',
          description: '문법 개념 완성과 실전 적용',
          features: ['음운론', '형태론', '통사론', '의미론'],
        },
      ],
    },
  };

  const currentMiddleContent = middleCategoryContent[middleCategory];
  const currentHighContent = highCategoryContent[highCategory];

  return (
    <div className="min-h-screen bg-neutral-950">
      <Navigation currentPage="curriculum" />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-10 md:pt-32 md:pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-amber-400/10 border border-amber-400/30 rounded-full mb-6">
            <span className="text-amber-400 text-sm font-semibold">KOREAN CURRICULUM</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="md:hidden">미래인재학원<br />국어 커리큘럼</span>
            <span className="hidden md:inline">미래인재학원 국어 커리큘럼</span>
          </h1>
        </div>
      </section>

      {/* Course Type Toggle Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col items-center">
            <div className="inline-flex gap-4 bg-gray-100 p-2 rounded-full">
              <button
                onClick={() => setCourseType('middle')}
                className={`px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer border-2 ${
                  courseType === 'middle'
                    ? 'bg-orange-500 text-white border-orange-500 shadow-lg'
                    : 'bg-white text-orange-500 border-orange-500 hover:bg-orange-50'
                }`}
              >
                중등과정
              </button>
              <button
                onClick={() => setCourseType('high')}
                className={`px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer border-2 ${
                  courseType === 'high'
                    ? 'bg-orange-500 text-white border-orange-500 shadow-lg'
                    : 'bg-white text-orange-500 border-orange-500 hover:bg-orange-50'
                }`}
              >
                고등과정
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Category Toggle Section - 중등과정 */}
      {courseType === 'middle' && (
        <section className="py-4 sm:py-10 bg-gray-50/95 sticky top-[80px] z-30 backdrop-blur-md border-b border-gray-200 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 w-full max-w-md sm:max-w-none">
                {middleCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setMiddleCategory(cat.id)}
                    className={`px-4 sm:px-10 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all duration-300 whitespace-nowrap cursor-pointer border-2 ${
                      middleCategory === cat.id
                        ? 'bg-orange-500 text-white border-orange-500 shadow-lg'
                        : 'bg-white text-orange-500 border-orange-500 hover:bg-orange-50'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Category Toggle Section - 고등과정 */}
      {courseType === 'high' && (
        <section className="py-4 sm:py-10 bg-gray-50/95 sticky top-[80px] z-30 backdrop-blur-md border-b border-gray-200 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-md">
                {highCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setHighCategory(cat.id)}
                    className={`px-4 sm:px-10 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all duration-300 whitespace-nowrap cursor-pointer border-2 ${
                      highCategory === cat.id
                        ? 'bg-orange-500 text-white border-orange-500 shadow-lg'
                        : 'bg-white text-orange-500 border-orange-500 hover:bg-orange-50'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 중등과정 Content */}
      {courseType === 'middle' && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="text-4xl font-bold text-gray-900 mb-4">{currentMiddleContent.title}</div>
              <p className="text-lg text-gray-600">{currentMiddleContent.description}</p>
            </div>

            {/* 어휘 카테고리일 때 교재 이미지 표시 */}
            {middleCategory === 'vocabulary' && (
              <div className="mb-12">
                <div className="space-y-10 max-w-4xl mx-auto">
                  {/* 중1 과정 */}
                  <div className="flex flex-col items-center gap-4">
                    <span className="inline-block px-5 py-3 bg-orange-500 text-white font-bold rounded-full text-center text-lg">중1 과정</span>
                    <div className="flex gap-3 sm:gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/c6863d3499fbc370100580d18f93784c.png" 
                            alt="중1 국어 어휘 교재 1학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">1학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">빠작 중학 국어 어휘1</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/0ca635f90a7934ac898c9c2ef5b007b4.png" 
                            alt="중1 국어 어휘 교재 2학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">2학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">빠작 중학 국어 비문학 어휘1</span>
                      </div>
                    </div>
                  </div>

                  {/* 중2 과정 */}
                  <div className="flex flex-col items-center gap-4">
                    <span className="inline-block px-5 py-3 bg-orange-500 text-white font-bold rounded-full text-center text-lg">중2 과정</span>
                    <div className="flex gap-3 sm:gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/ebbec7665f749fce5da035ded0c2828d.png" 
                            alt="중2 국어 어휘 교재 1학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">1학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">빠작 중학 국어 어휘2</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/6b296ed9aff2d76facf7672a5e382b92.png" 
                            alt="중2 국어 어휘 교재 2학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">2학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">빠작 중학 국어 한자 어휘</span>
                      </div>
                    </div>
                  </div>

                  {/* 중3 과정 */}
                  <div className="flex flex-col items-center gap-4">
                    <span className="inline-block px-5 py-3 bg-orange-500 text-white font-bold rounded-full text-center text-lg">중3 과정</span>
                    <div className="flex gap-3 sm:gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/044e5ceac67e458eed2abfa018a34515.png" 
                            alt="중3 국어 어휘 교재 1학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">1학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">빠작 중학 국어 어휘3</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/ccb1b58ae7035a386476143b31834103.png" 
                            alt="중3 국어 어휘 교재 2학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">2학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">매3력 어휘력,독서력,국어력</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 문법 카테고리일 때 교재 이미지 표시 */}
            {middleCategory === 'grammar' && (
              <div className="mb-12">
                <div className="space-y-10 max-w-4xl mx-auto">
                  {/* 중1 과정 */}
                  <div className="flex flex-col items-center gap-4">
                    <span className="inline-block px-5 py-3 bg-orange-500 text-white font-bold rounded-full text-center text-lg">중1 과정</span>
                    <div className="flex gap-3 sm:gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/3fc32654725ffda23eeda8752a85e5a8.png" 
                            alt="중1 국어 문법 교재 1학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">1학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">똑독 중학 국어 문법(기본편)</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/de78f592d29e05ad8e511c15ed43984e.png" 
                            alt="중1 국어 문법 교재 2학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">2학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">문법 DNA 깨우기</span>
                      </div>
                    </div>
                  </div>

                  {/* 중2 과정 */}
                  <div className="flex flex-col items-center gap-4">
                    <span className="inline-block px-5 py-3 bg-orange-500 text-white font-bold rounded-full text-center text-lg">중2 과정</span>
                    <div className="flex gap-3 sm:gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/b4a503364f0372649af9c0563f49a20d.png" 
                            alt="중2 국어 문법 교재 1학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">1학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">빠작 중학 국어 첫 문법</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/fd51c3f3073ff986e39bfe46f6102b9b.png" 
                            alt="중2 국어 문법 교재 2학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">2학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">똑독 중학 국어 문법(실력편)</span>
                      </div>
                    </div>
                  </div>

                  {/* 중3 과정 */}
                  <div className="flex flex-col items-center gap-4">
                    <span className="inline-block px-5 py-3 bg-orange-500 text-white font-bold rounded-full text-center text-lg">중3 과정</span>
                    <div className="flex gap-3 sm:gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/311d72ed951c9d79c10ede5dcef7c424.png" 
                            alt="중3 국어 문법 교재 1학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">1학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">문제로 국어 문법</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/7d1ada7fbb9258d840edbfd9f7f9446f.png" 
                            alt="중3 국어 문법 교재 2학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">2학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">빠작 중학 국어 문법</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 문학 카테고리일 때 교재 이미지 표시 */}
            {middleCategory === 'literature' && (
              <div className="mb-12">
                <div className="space-y-10 max-w-4xl mx-auto">
                  {/* 중1 과정 */}
                  <div className="flex flex-col items-center gap-4">
                    <span className="inline-block px-5 py-3 bg-orange-500 text-white font-bold rounded-full text-center text-lg">중1 과정</span>
                    <div className="flex gap-3 sm:gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/c4ae257c600ccc78561c9e55b91abbb7.png" 
                            alt="중1 국어 문학 교재 1학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">1학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">빠작 중학 국어 문학 독해1</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/6d3033069ee480248663440fbc9290d4.png" 
                            alt="중1 국어 문학 교재 2학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">2학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">중등 수능 독해(국어 문학 독해1)</span>
                      </div>
                    </div>
                  </div>

                  {/* 중2 과정 */}
                  <div className="flex flex-col items-center gap-4">
                    <span className="inline-block px-5 py-3 bg-orange-500 text-white font-bold rounded-full text-center text-lg">중2 과정</span>
                    <div className="flex gap-3 sm:gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/49dd3254b462a5f973646fe1defd90a2.png" 
                            alt="중2 국어 문학 교재 1학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">1학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">빠작 중학 국어 문학 독해2</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/1b4d31ae886ab28e591e4725e3ac3d6a.png" 
                            alt="중2 국어 문학 교재 2학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">2학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">중등 수능 독해(국어 문학 독해2)</span>
                      </div>
                    </div>
                  </div>

                  {/* 중3 과정 */}
                  <div className="flex flex-col items-center gap-4">
                    <span className="inline-block px-5 py-3 bg-orange-500 text-white font-bold rounded-full text-center text-lg">중3 과정</span>
                    <div className="flex gap-3 sm:gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/6e72bb1ca99c899c75168b04c1b0ed9c.png" 
                            alt="중3 국어 문학 교재 1학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">1학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">중등 수능 독해(국어 문학 독해3)</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/035dc3b6bc90287bb32c1f8184dd0b8e.png" 
                            alt="중3 국어 문학 교재 2학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">2학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">100인의 지혜 문학</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 비문학 카테고리일 때 교재 이미지 표시 */}
            {middleCategory === 'nonLiterature' && (
              <div className="mb-12">
                <div className="space-y-10 max-w-4xl mx-auto">
                  {/* 중1 과정 */}
                  <div className="flex flex-col items-center gap-4">
                    <span className="inline-block px-5 py-3 bg-orange-500 text-white font-bold rounded-full text-center text-lg">중1 과정</span>
                    <div className="flex gap-3 sm:gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/b164045365f7243fd1a0ce2a90777d25.png" 
                            alt="중1 국어 비문학 교재 1학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">1학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">빠작 중학 국어 비문학 독해1</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/55c81e95f659801faa8a8c968031d6fd.png" 
                            alt="중1 국어 비문학 교재 2학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">2학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">중등 수능 독해(국어 비문학 독해1)</span>
                      </div>
                    </div>
                  </div>

                  {/* 중2 과정 */}
                  <div className="flex flex-col items-center gap-4">
                    <span className="inline-block px-5 py-3 bg-orange-500 text-white font-bold rounded-full text-center text-lg">중2 과정</span>
                    <div className="flex gap-3 sm:gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/8679e3fd205c8fbe4612632c5aebe37a.png" 
                            alt="중2 국어 비문학 교재 1학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">1학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">빠작 중학 국어 비문학 독해2</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/1b4d31ae886ab28e591e4725e3ac3d6a.png" 
                            alt="중2 국어 비문학 교재 2학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">2학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">중등 수능 독해(국어 비문학 독해2)</span>
                      </div>
                    </div>
                  </div>

                  {/* 중3 과정 */}
                  <div className="flex flex-col items-center gap-4">
                    <span className="inline-block px-5 py-3 bg-orange-500 text-white font-bold rounded-full text-center text-lg">중3 과정</span>
                    <div className="flex gap-3 sm:gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/fbfc1f91c5aca07d92a527ea9f820dfc.png" 
                            alt="중3 국어 비문학 교재 1학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">1학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">중등 수능 독해(국어 비문학 독해3)</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-36 h-48 sm:w-52 sm:h-72 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                          <img 
                            src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/5ce047f824a6e4becd25c45b57582113.png" 
                            alt="중3 국어 비문학 교재 2학기" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="mt-3 text-gray-700 font-semibold text-sm sm:text-base">2학기</span>
                        <span className="text-gray-600 text-xs sm:text-sm">100인의 지혜 독서</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 고등과정 Content */}
      {courseType === 'high' && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">{currentHighContent.title}</h2>
              <div className="inline-block px-4 py-2 bg-orange-500 text-white font-semibold rounded-full mb-3">
                {highCategory === 'regular' ? '주2회 수업' : '주1회 수업'}
              </div>
              <p className="text-lg text-gray-600">{currentHighContent.description}</p>
              
              {/* 클리닉 카테고리일 때 세 개의 원 표시 */}
              {highCategory === 'clinic' && (
                <div className="flex justify-center gap-8 mt-8">
                  <div className="w-40 h-40 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-center text-base leading-tight px-3">어휘 점검테스트</span>
                  </div>
                  <div className="w-40 h-40 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-center text-base leading-tight px-3">실전<br className="md:hidden" /> 모의고사<br className="md:hidden" /> 연습</span>
                  </div>
                  <div className="w-40 h-40 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-center text-base leading-tight px-3">숙제<br className="md:hidden" /> 미완료시<br className="md:hidden" />완료 후<br className="md:hidden" /> 귀가</span>
                  </div>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {currentHighContent.items.map((item, index) => (
                <>
                  {highCategory === 'clinic' && (index === 0 || index === 1 || index === 2 || index === 3 || index === 4) ? null : (
                    <div
                      key={index}
                      className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-300"
                    >
                      {highCategory !== 'clinic' && (
                        <div className="bg-gradient-to-r from-orange-500 to-orange-400 p-4">
                          <h4 className="text-white font-bold text-lg">{item.title}</h4>
                          <p className="text-white/80 text-sm">{item.grade}</p>
                        </div>
                      )}
                      <div className="p-6">
                        <ul className="space-y-3">
                          {item.content.map((content, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <i className="ri-check-line text-orange-500 mt-0.5"></i>
                              <span className="text-gray-700">{content}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </>
              ))}
            </div>

            {/* 자체교재 섹션 - 정규수업일 때만 표시 */}
            {highCategory === 'regular' && (
              <div className="mt-20">
                <div className="text-center mb-12">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">내신 자체 교재</h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* 교재 이미지 */}
                  <div className="flex justify-center">
                    <div className="w-64 h-80 bg-white rounded-2xl overflow-hidden shadow-xl border-2 border-orange-200">
                      <img 
                        src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/03361ed216ff068974038d0a3f8e1b48.png" 
                        alt="옥정고등학교 기말고사 대비를 위한, 핵심 개념부터 문제까지 한번에 정리한다! 옥정기 가말고사 Support" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  
                  {/* 설명 */}
                  <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-8 border border-orange-100">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">
                      학교별 <span className="text-orange-500">맞춤 교재</span>
                    </h4>
                    <div className="space-y-4">
                      <p className="text-gray-700 leading-relaxed">
                        학교별 자체 제작 교재를 제작해서, 내신 수업을 진행합니다.
                      </p>
                      <div className="pt-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                          <span className="text-gray-700">학교별 진도에 맞춘 커리큘럼</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                          <span className="text-gray-700">출제 경향 분석 반영</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                          <span className="text-gray-700">내신 완벽 대비</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 수능 수업 교재 섹션 - 정규수업일 때만 표시 */}
            {highCategory === 'regular' && (
              <div className="mt-20">
                <div className="text-center mb-12">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">수능 수업 교재</h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* 모의고사 교재 이미지 - PC: 3개 나란히, 모바일: 슬라이드 */}
                  {/* PC 버전 */}
                  <div className="hidden md:flex justify-center gap-4">
                    {bookImages.map((book, index) => (
                      <div key={index} className="w-40 h-56 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-orange-400 transition-all">
                        <img 
                          src={book.src} 
                          alt={book.alt} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  
                  {/* 모바일 버전 - 자동 슬라이드 */}
                  <div className="md:hidden flex flex-col items-center">
                    <div className="relative w-48 h-64 overflow-hidden">
                      <div 
                        className="flex transition-transform duration-500 ease-in-out h-full"
                        style={{ transform: `translateX(-${currentBookSlide * 100}%)` }}
                      >
                        {bookImages.map((book, index) => (
                          <div key={index} className="w-48 h-64 flex-shrink-0 bg-white rounded-xl overflow-hidden shadow-lg border-2 border-gray-200">
                            <img 
                              src={book.src} 
                              alt={book.alt} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* 슬라이드 인디케이터 */}
                    <div className="flex gap-2 mt-4">
                      {bookImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentBookSlide(index)}
                          className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                            currentBookSlide === index ? 'bg-orange-500 w-4' : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* 설명 */}
                  <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-8 border border-orange-100">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">
                      이감 <span className="text-orange-500">모의고사 교재</span>
                    </h4>
                    <div className="space-y-4">
                      <p className="text-gray-700 leading-relaxed">
                        이감으로 기출, 이감 모의고사는<br />
                        학군지에서 쓰는 국어 전문 학원 교재입니다.
                      </p>
                      <div className="pt-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                          <span className="text-gray-700">수능 기출 완벽 분석</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                          <span className="text-gray-700">실전 모의고사 훈련</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                          <span className="text-gray-700">국어 전문학원 전용 교재</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            국어 실력 향상의 시작
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            <span className="md:hidden">체계적인 커리큘럼과 전문 강사진이<br />함께합니다</span>
            <span className="hidden md:inline">체계적인 커리큘럼과 전문 강사진이 함께합니다</span>
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-10 py-4 bg-amber-400 text-black text-lg font-bold rounded-full hover:bg-amber-300 transition-all shadow-xl shadow-amber-400/20 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-check-line text-xl"></i>
            입학상담
          </Link>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
